import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { MessageSquare, Edit2, Trash2, X, Check, Send } from 'lucide-react';

const GREEN = '#0F6E56';

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'agora';
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)} h`;
  if (diff < 7 * 86400) return `há ${Math.floor(diff / 86400)} dias`;
  return new Date(iso).toLocaleDateString('pt-BR');
}

function initial(name) {
  return (name || '?').trim().charAt(0).toUpperCase();
}

function CommentItem({ comment, currentUser, onEdited, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const [saving, setSaving] = useState(false);
  const isAuthor = currentUser?.user_id === comment.author_user_id;
  const isAdmin = (currentUser?.role || '').toUpperCase() === 'ADMIN';
  const canEdit = isAuthor;
  const canDelete = isAuthor || isAdmin;

  const handleSave = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (trimmed === comment.body) { setEditing(false); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/comments/${comment.comment_id}`, { body: trimmed });
      onEdited(data);
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Excluir este comentário?')) return;
    try {
      await api.delete(`/comments/${comment.comment_id}`);
      onDeleted(comment.comment_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  return (
    <div className="flex gap-3 py-3" data-testid={`comment-${comment.comment_id}`}>
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-white text-xs font-semibold"
        style={{ backgroundColor: GREEN }}
        aria-hidden
      >
        {initial(comment.author_name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-900">{comment.author_name}</span>
          <span className="text-xs text-gray-500">{timeAgo(comment.created_at)}</span>
          {comment.edited && (
            <span className="text-[10px] text-gray-400 italic">(editado)</span>
          )}
          <div className="ml-auto flex items-center gap-1">
            {canEdit && !editing && (
              <button
                onClick={() => { setDraft(comment.body); setEditing(true); }}
                className="text-gray-400 hover:text-emerald-700 p-1 rounded"
                title="Editar"
                data-testid={`comment-edit-${comment.comment_id}`}
              >
                <Edit2 className="w-3.5 h-3.5" />
              </button>
            )}
            {canDelete && !editing && (
              <button
                onClick={handleDelete}
                className="text-gray-400 hover:text-red-600 p-1 rounded"
                title="Excluir"
                data-testid={`comment-delete-${comment.comment_id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {editing ? (
          <div className="mt-1">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              className="text-sm"
              data-testid={`comment-edit-input-${comment.comment_id}`}
            />
            <div className="mt-2 flex items-center gap-2">
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                style={{ backgroundColor: GREEN }}
                className="text-white hover:opacity-90"
                data-testid={`comment-save-${comment.comment_id}`}
              >
                <Check className="w-3.5 h-3.5 mr-1" /> Salvar
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing(false)}
              >
                <X className="w-3.5 h-3.5 mr-1" /> Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap break-words" data-testid={`comment-body-${comment.comment_id}`}>
            {comment.body}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Reusable comments thread.
 *
 * Props:
 *  - parentType: "project" | "task"
 *  - parentId: string
 *  - currentUser: { user_id, role, full_name }
 *  - compact (bool, default false) — render in a tighter style for inline use
 *  - onCountChange (fn, optional) — called with the new count after add/delete
 */
export default function Comments({
  parentType, parentId, currentUser, compact = false, onCountChange,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/comments', {
          params: { parent_type: parentType, parent_id: parentId },
        });
        if (cancelled) return;
        setItems(data || []);
        onCountChange?.((data || []).length);
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.detail || 'Erro ao carregar comentários');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentType, parentId]);

  const handlePost = async () => {
    const body = draft.trim();
    if (!body) return;
    setPosting(true);
    try {
      const { data } = await api.post('/comments', {
        parent_type: parentType, parent_id: parentId, body,
      });
      const next = [...items, data];
      setItems(next);
      onCountChange?.(next.length);
      setDraft('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao publicar');
    } finally {
      setPosting(false);
    }
  };

  const handleEdited = (updated) => {
    setItems((prev) => prev.map((c) => (c.comment_id === updated.comment_id ? updated : c)));
  };
  const handleDeleted = (id) => {
    setItems((prev) => {
      const next = prev.filter((c) => c.comment_id !== id);
      onCountChange?.(next.length);
      return next;
    });
  };

  return (
    <div className={compact ? '' : 'space-y-3'} data-testid={`comments-${parentType}-${parentId}`}>
      {!compact && (
        <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
          <MessageSquare className="w-4 h-4" style={{ color: GREEN }} />
          Discussão {items.length > 0 && <span className="text-gray-500 font-normal">({items.length})</span>}
        </div>
      )}

      <div className="divide-y divide-gray-100">
        {loading ? (
          <div className="py-4 text-xs text-gray-500">Carregando...</div>
        ) : items.length === 0 ? (
          <div className="py-3 text-xs text-gray-500 italic">
            Nenhum comentário ainda. Seja o primeiro a registrar uma observação.
          </div>
        ) : (
          items.map((c) => (
            <CommentItem
              key={c.comment_id}
              comment={c}
              currentUser={currentUser}
              onEdited={handleEdited}
              onDeleted={handleDeleted}
            />
          ))
        )}
      </div>

      <div className="pt-3 border-t border-gray-100">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Escreva uma observação interna..."
          rows={compact ? 2 : 3}
          className="text-sm"
          data-testid={`comments-input-${parentType}-${parentId}`}
        />
        <div className="mt-2 flex items-center justify-between">
          <span className="text-[11px] text-gray-400">
            Visível apenas para administradores e consultores.
          </span>
          <Button
            size="sm"
            onClick={handlePost}
            disabled={posting || !draft.trim()}
            style={{ backgroundColor: GREEN }}
            className="text-white hover:opacity-90"
            data-testid={`comments-submit-${parentType}-${parentId}`}
          >
            {posting ? '...' : <><Send className="w-3.5 h-3.5 mr-1.5" /> Publicar</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
